import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type SipUser } from "../../lib/api";
import type { AgentAccount } from "../../types";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { Panel } from "../../components/ui/Panel";
import { inputClass } from "./shared";

/**
 * Add and manage the people who use Pulse.
 *
 * "Users", not "agents", and the role beside each of them is not per product.
 * There is one `users` table behind the Helpdesk and the CRM, so a manager is a
 * manager of Pulse -- they can configure both. Calling them agents suggested a
 * helpdesk job title, and having this page inside the Helpdesk suggested the
 * permission stopped at its edge; neither was ever true of the data.
 *
 * Adding somebody creates their account immediately, before they have ever
 * logged in, so they can be assigned tickets and put in teams the moment they
 * start. Their first sign-in claims it.
 *
 * It is half an invitation, and the form says so. Signing in needs a Pulse seat
 * in identify as well, which is assigned in the admin console and which Pulse
 * cannot see from here -- so a row added without one belongs to somebody who
 * can be given work and will never turn up to collect it. The list marks them
 * "pending" until a sign-in proves both halves were done.
 */
/**
 * What identify says about one agent, when it says anything worth saying.
 *
 * Renders nothing for "active" -- the overwhelmingly common case, and a list
 * where every row carries a green tick is a list nobody scans. It also renders
 * nothing for "unknown" or "unconfigured": we could not reach identify, or
 * there is none to reach, both of which are our situation and not this
 * person's. A row that shrugs at you is worse than a row that says nothing, so
 * the note above the list carries that instead -- and only for "unknown",
 * since an unconfigured local checkout has nothing to report.
 *
 * The two that do render are the two somebody has to act on, and they need
 * different actions -- which is the entire reason this exists, because before
 * it they were the same grey "not signed in yet".
 */
function SeatBadge({ state }: { state: AgentAccount["seatState"] }) {
  if (state !== "no_seat" && state !== "no_account") return null;

  const copy =
    state === "no_seat"
      ? { label: "No Pulse seat", title: "This person has a NetXL account but no Pulse seat. Assign one in the admin console." }
      : { label: "No NetXL account", title: "identify has no account for this address in this organisation. Either they have not been invited there yet, or the address here is wrong." };

  return (
    <span
      title={copy.title}
      className="shrink-0 rounded bg-amber-100 px-1 text-[10px] uppercase text-amber-800 dark:bg-amber-950 dark:text-amber-300"
    >
      {copy.label}
    </span>
  );
}

export function AgentSettings() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<"agent" | "admin">("agent");
  const [error, setError] = useState<string | null>(null);

  const agents = useQuery({ queryKey: queryKeys.agents, queryFn: api.agents });

  /*
   * The phone system's people, for the picker on each row. Asked once for the
   * whole list rather than per row, and allowed to fail: a settings page that
   * will not load because the phone system is busy is worse than one without
   * the picker.
   */
  const sipUsers = useQuery({
    queryKey: queryKeys.sipUsers,
    queryFn: api.sipUsers,
    staleTime: 60_000,
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents });
    void queryClient.invalidateQueries({ queryKey: queryKeys.users });
    /* Each SIP user says who it is linked to, and the call history names
       people through the same link. */
    void queryClient.invalidateQueries({ queryKey: queryKeys.phoneCallsRoot });
  };

  const invite = useMutation({
    mutationFn: () => api.inviteAgent({ email: email.trim(), name: name.trim(), role }),
    onSuccess: () => {
      setEmail("");
      setName("");
      setError(null);
      invalidate();
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not add that person"),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: {
        name?: string;
        role?: "agent" | "admin";
        isActive?: boolean;
        yayUserUuid?: string | null;
      };
    }) => api.updateAgent(id, patch),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    /*
     * Shown, and the list refetched, so a refused link -- a phone user already
     * linked to somebody else -- snaps back to what is really stored rather
     * than sitting in the control looking saved.
     */
    onError: (cause: unknown) => {
      setError(cause instanceof Error ? cause.message : "Could not save that change");
      invalidate();
    },
  });

  return (
    <Panel
      collapsible
      title="Users"
    >
      <form
        className="mb-4 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim() && name.trim()) invite.mutate();
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Full name"
          className={cn(inputClass, "min-w-40 flex-1")}
        />
        <input
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@netxl.com"
          className={cn(inputClass, "min-w-52 flex-1")}
        />
        <select
          value={role}
          onChange={(event) => setRole(event.target.value as "agent" | "admin")}
          className={cn(inputClass, "w-28")}
        >
          <option value="agent">User</option>
          <option value="admin">Manager</option>
        </select>
        <Button
          type="submit"
          disabled={!email.trim() || !name.trim() || invite.isPending}
        >
          {invite.isPending ? "Adding…" : "Add user"}
        </Button>
      </form>

      {/*
        Stated at the point of the decision rather than in a document nobody
        opens. Sign-in used to follow from the address alone, so adding a row
        here really was the whole job; it is not any more, and the person most
        likely to be surprised by that is the one standing at this form.
      */}
      <p className="mb-3 text-xs text-muted">
        Adding someone here does not let them in on its own — they also need a
        Pulse seat, assigned in the NetXL admin console. The list below says
        which of the two each person is missing.
      </p>

      {/*
        Said once, at the list, rather than as a badge on every row: the lookup
        failing is one fact about this page load, not a fact about forty people.
      */}
      {(agents.data ?? []).some((agent) => agent.seatState === "unknown") ? (
        <p className="mb-3 text-xs text-muted">
          Could not reach the NetXL account service, so seat status is not shown
          below. Everything else on this page is unaffected.
        </p>
      ) : null}

      {error ? <p className="mb-3 text-xs text-red-600">{error}</p> : null}

      <ul className="divide-y divide-line">
        {(agents.data ?? []).map((agent) => (
          /*
            Two lines on a phone: who, then the controls. On one line the
            role select, the phone picker and Deactivate left the name box
            nothing at all, and the badges beside it were cut mid-word -- so
            the row said "NOT SIGNED IN YE" about somebody it did not name.
          */
          <li
            key={agent.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2 md:flex-nowrap"
          >
            <div className="min-w-0 basis-full md:basis-auto md:flex-1">
              <div className="flex items-baseline gap-2">
                {/*
                  Editable in place. The name is typed by whoever sent the
                  invitation and nothing corrects it afterwards, yet it is what
                  customers see over outbound mail -- so a misspelling needs to
                  be fixable by the person who notices, not by a database edit.
                */}
                <AgentName
                  agent={agent}
                  onRename={(name) => update.mutate({ id: agent.id, patch: { name } })}
                />
                {/*
                  Two different facts, deliberately shown as two badges rather
                  than one combined status.

                  `seatState` is whether identify will let them in; `pending` is
                  whether they have got round to it. Somebody can be perfectly
                  set up and simply not have logged in yet, which is not a
                  problem anybody needs to fix -- collapsing the two would turn
                  every new starter into a warning for their first morning.
                */}
                <SeatBadge state={agent.seatState} />
                {agent.pending && agent.seatState !== "no_account" ? (
                  <span className="shrink-0 rounded bg-surface-2 px-1 text-[10px] uppercase text-muted-on-tint">
                    Not signed in yet
                  </span>
                ) : null}
                {!agent.isActive ? (
                  <span className="shrink-0 text-[10px] uppercase text-faint">
                    Deactivated
                  </span>
                ) : null}
              </div>
              <span className="truncate text-xs text-muted">{agent.email}</span>
            </div>

            {/*
              Beside the role rather than behind a dialog, because it is set
              once per person and read on every call: a link nobody can find to
              make is a call history that never names anybody. Absent, rather
              than disabled, when no phone system is connected.
            */}
            {sipUsers.data?.enabled ? (
              <PhoneUserPicker
                agent={agent}
                sipUsers={sipUsers.data.users}
                onChange={(yayUserUuid) =>
                  update.mutate({ id: agent.id, patch: { yayUserUuid } })
                }
              />
            ) : null}

            <select
              value={agent.role}
              onChange={(event) =>
                update.mutate({
                  id: agent.id,
                  patch: { role: event.target.value as "agent" | "admin" },
                })
              }
              className={cn(inputClass, "w-28")}
            >
              <option value="agent">User</option>
              <option value="admin">Manager</option>
            </select>

            {/* Deactivate rather than delete: authorship on historical
                messages must survive somebody leaving. */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                update.mutate({
                  id: agent.id,
                  patch: { isActive: !agent.isActive },
                })
              }
            >
              {agent.isActive ? "Deactivate" : "Reactivate"}
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * An agent's name, edited in place.
 *
 * A text box rather than a name with a pencil beside it: there is one field,
 * the whole row is already a form of switches and buttons, and an edit mode
 * would be a state to enter and leave for a single word.
 *
 * Commits on Enter and on the way out, which are both "I have finished typing".
 * Escape puts back what was there -- the only way to abandon a change once the
 * box is holding it.
 */
function AgentName({
  agent,
  onRename,
}: {
  agent: { id: number; name: string; isActive: boolean };
  onRename: (name: string) => void;
}) {
  const [value, setValue] = useState(agent.name);

  /*
   * Follows the server when the row changes underneath -- another manager
   * renaming somebody, or this edit coming back confirmed. Skipped while the
   * box differs from what was last saved, so a refetch mid-sentence cannot
   * overwrite what is being typed.
   */
  const [saved, setSaved] = useState(agent.name);
  if (agent.name !== saved && value === saved) {
    setSaved(agent.name);
    setValue(agent.name);
  }

  const commit = (): void => {
    const next = value.trim();
    if (!next) {
      // An empty name would leave a blank byline on outbound mail. Nothing to
      // save, so put the old one back rather than refusing with a message.
      setValue(saved);
      return;
    }
    if (next === saved) return;
    setSaved(next);
    setValue(next);
    onRename(next);
  };

  return (
    <input
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === "Escape") {
          setValue(saved);
          event.currentTarget.blur();
        }
      }}
      aria-label={`Name for ${saved}`}
      className={cn(
        "min-w-0 flex-1 truncate rounded border border-transparent bg-transparent px-1 py-0.5 text-sm",
        "hover:border-line focus:border-line focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        agent.isActive ? "text-content" : "text-faint",
      )}
    />
  );
}

/**
 * Which of the phone system's users this person is.
 *
 * Chosen from Yay's own list rather than typed, and stored as the SIP user's
 * uuid: an extension can be renumbered or handed on, where the uuid names one
 * SIP user for as long as it exists. The link is read whenever calls are, so
 * choosing somebody here attributes their whole call history at once, and
 * choosing again corrects it.
 *
 * A SIP user already linked to somebody else is listed but not offered, with
 * their name beside it -- the server would refuse it, and saying who has it
 * is what tells an admin which row to fix. A link to a SIP user Yay no longer
 * lists is kept as an option of its own, so opening the page does not
 * quietly look as if it had been cleared.
 */
function PhoneUserPicker({
  agent,
  sipUsers,
  onChange,
}: {
  agent: { id: number; name: string; yayUserUuid: string | null };
  sipUsers: SipUser[];
  onChange: (yayUserUuid: string | null) => void;
}) {
  const current = agent.yayUserUuid ?? "";
  const known = sipUsers.some((sip) => sip.uuid === current);

  return (
    <select
      value={current}
      onChange={(event) => onChange(event.target.value || null)}
      title="Their user on the phone system, which is how calls are attributed to them"
      aria-label={`Phone user for ${agent.name}`}
      /* Takes what the role select and Deactivate leave on a phone's second
         line, rather than a fixed width that pushed them off its end. */
      className={cn(inputClass, "min-w-0 flex-1 md:w-44 md:flex-none")}
    >
      <option value="">No phone user</option>
      {current && !known ? (
        <option value={current}>No longer on the phone system</option>
      ) : null}
      {sipUsers.map((sip) => {
        const takenBy =
          sip.linkedUser && sip.linkedUser.id !== agent.id ? sip.linkedUser : null;
        const label = [sip.name ?? "Unnamed", sip.extension ? `ext. ${sip.extension}` : null]
          .filter(Boolean)
          .join(" · ");

        return (
          <option key={sip.uuid} value={sip.uuid} disabled={takenBy !== null}>
            {takenBy ? `${label} — ${takenBy.name}` : label}
          </option>
        );
      })}
    </select>
  );
}
